/**
 * Yahoo検索広告 クリックID捕捉・受け渡しスクリプト
 *
 * 目的:
 *   Checkout Extensibility移行後はcheckout.liquid上のタグ計測が動作しないため、
 *   広告クリックIDを注文のnote_attributesまで運び、サーバーサイドから
 *   Yahoo検索広告コンバージョンAPIへ送信する構成に移行します。
 *   本スクリプトはそのうちストアフロント側（捕捉と受け渡し）を担当します。
 *
 * 動作:
 *   1. 捕捉: ページ着地時にURLクエリの yclid / sa_p / sa_cc / sa_t / sa_ra を
 *      localStorage（キー: ly_click）へタイムスタンプ付きJSONで保存します。
 *      新しいクリックは常に上書きします（ラストクリック優先）。
 *   2. 受け渡し: /cart へPOSTするフォーム（カートページ・カートドロワー）の
 *      submit時に hidden input「attributes[ly_click]」を同期注入し、
 *      注文の note_attributes に載せます。
 *
 * 注意事項:
 *   - 既存のytag（ycl_cookie）とは独立して動作します。並行稼働期間中も干渉しません。
 *   - 保存データの有効期限は90日です（コンバージョンAPIのevent_time受付範囲に準拠）。
 *   - 計測処理がストア本体の購入動線を壊さないことを最優先とし、ストレージ例外時は
 *     計測を断念します（Fail-Fast原則の意図的な例外。購入 > 計測のため）。
 *   - 商品ページの動的チェックアウトボタン（Buy it now）経由の注文はカートフォームを
 *     経由しないため、attributes を受け渡せません（既知の制約）。
 */
(function () {
  'use strict';

  /** localStorageの保存キー */
  var STORAGE_KEY = 'ly_click';

  /** カートフォームへ注入するcart attribute入力欄の名前 */
  var ATTRIBUTE_INPUT_NAME = 'attributes[ly_click]';

  /**
   * 捕捉対象のURLパラメータ。
   * yclidが主識別子、sa_*はyclidが取得できない場合のフォールバック識別子です。
   */
  var CLICK_PARAM_KEYS = ['yclid', 'sa_p', 'sa_cc', 'sa_t', 'sa_ra'];

  /** 保存データの有効期限（90日）。期限切れはsubmit時に注入されず、削除されます。 */
  var TTL_MS = 90 * 24 * 60 * 60 * 1000;

  /**
   * URLクエリ文字列から捕捉対象のクリックパラメータを抽出します。
   * 対象パラメータが1つもない場合はnullを返します。
   */
  function extractClickParams(search) {
    var params = new URLSearchParams(search);
    var found = null;
    CLICK_PARAM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        found = found || {};
        found[key] = value;
      }
    });
    return found;
  }

  /**
   * クリックパラメータを取得時刻（_ts）付きでlocalStorageへ保存します。
   * 対象パラメータがない場合は既存の保存値を維持します（上書きしない）。
   * ストレージ例外は伝播させません（購入動線保護のため計測を断念）。
   */
  function saveClickParams(storage, search, now) {
    try {
      var found = extractClickParams(search);
      if (!found) {
        return;
      }
      found._ts = now;
      storage.setItem(STORAGE_KEY, JSON.stringify(found));
    } catch (error) {
      // localStorage無効環境・容量超過などでは計測を断念します
    }
  }

  /**
   * 保存済みクリックデータを検証付きで読み出します。
   * 有効期限内であれば保存時のJSON文字列をそのまま返します。
   * 期限切れ・破損データはストレージから削除してnullを返します。
   */
  function readValidClick(storage, now) {
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      var parsed = JSON.parse(raw);
      if (typeof parsed._ts !== 'number' || now - parsed._ts > TTL_MS) {
        storage.removeItem(STORAGE_KEY);
        return null;
      }
      return raw;
    } catch (error) {
      // 破損データは削除して計測を断念します
      try {
        storage.removeItem(STORAGE_KEY);
      } catch (removeError) {
        // 削除も失敗する環境では何もできないため無視します
      }
      return null;
    }
  }

  /**
   * /cartへPOSTするカートフォームかどうかを判定します。
   * ロケールプレフィックス付きURL（例: /ja/cart）や絶対URLも許容します。
   */
  function isCartForm(form) {
    if (String(form.method || '').toLowerCase() !== 'post') {
      return false;
    }
    var action = form.getAttribute('action') || '';
    var path = action.split('?')[0];
    return /(^|\/)cart$/.test(path);
  }

  /**
   * submitイベントを受け、カートフォームであればhidden inputを注入・更新します。
   * 同期的にDOMへ追加するため、直後のフォーム送信データに確実に含まれます。
   */
  function handleSubmit(event, doc, storage, now) {
    var form = event.target;
    if (!form || !isCartForm(form)) {
      return;
    }
    var value = readValidClick(storage, now);
    if (!value) {
      return;
    }
    var input = form.querySelector('input[name="' + ATTRIBUTE_INPUT_NAME + '"]');
    if (!input) {
      input = doc.createElement('input');
      input.type = 'hidden';
      input.name = ATTRIBUTE_INPUT_NAME;
      form.appendChild(input);
    }
    input.value = value;
  }

  /**
   * 初期化処理。着地URLからの捕捉と、submitイベントの委譲リスナー登録を行います。
   * テストではlocation/document/localStorage/nowを差し替えたオブジェクトを渡せます。
   * （nowは現在時刻ミリ秒を返す関数。省略時はDate.nowを使用します）
   * 戻り値は登録したリスナーの解除関数です（テストでの後片付け用）。
   */
  function init(win) {
    var noop = function () {};
    var storage;
    try {
      storage = win.localStorage;
    } catch (error) {
      // localStorageアクセス自体が禁止された環境では計測を断念します
      return noop;
    }
    if (!storage) {
      return noop;
    }
    var now =
      win.now ||
      function () {
        return Date.now();
      };

    saveClickParams(storage, win.location.search, now());

    // カートドロワーはJSで再描画されるため、documentへの委譲（capture）で確実に捕まえます
    var onSubmit = function (event) {
      handleSubmit(event, win.document, storage, now());
    };
    win.document.addEventListener('submit', onSubmit, true);

    return function dispose() {
      win.document.removeEventListener('submit', onSubmit, true);
    };
  }

  var api = {
    extractClickParams: extractClickParams,
    saveClickParams: saveClickParams,
    readValidClick: readValidClick,
    init: init,
  };

  if (typeof module !== 'undefined' && module.exports) {
    // テスト環境（vitest）向けのエクスポート。自動初期化は行いません
    module.exports = api;
  } else if (typeof window !== 'undefined') {
    window.lyClickTracking = api;
    init(window);
  }
})();
